import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Repeat, Pencil, Trash2, Loader2, CheckCircle, Play, Calendar } from "lucide-react";
import { toast } from "sonner";

const INTERVAL_LABELS = {
  weekly: "Viikoittain",
  monthly: "Kuukausittain",
  quarterly: "Neljännesvuosittain",
  yearly: "Vuosittain",
};

function RecurringForm({ recurring, settings, onSaved, onClose }) {
  const isEdit = !!recurring;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: recurring?.name || "",
    interval: recurring?.interval || "monthly",
    next_invoice_date: recurring?.next_invoice_date || new Date().toISOString().split("T")[0],
    is_active: recurring?.is_active ?? true,
    recipient_name: recurring?.recipient_name || "",
    recipient_email: recurring?.recipient_email || "",
    description: recurring?.items?.[0]?.description || "",
    amount: recurring?.items?.[0]?.unit_price || "",
    vat_percent: recurring?.items?.[0]?.vat_percent || settings?.default_vat_percent || 25.5,
    notes: recurring?.notes || "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const save = async () => {
    if (!form.name || !form.recipient_name || !form.amount) {
      toast.error("Täytä pakolliset kentät");
      return;
    }
    setLoading(true);
    try {
      const unitPrice = parseFloat(form.amount) || 0;
      const data = {
        name: form.name,
        interval: form.interval,
        next_invoice_date: form.next_invoice_date,
        is_active: form.is_active,
        recipient_name: form.recipient_name,
        recipient_email: form.recipient_email,
        notes: form.notes,
        items: [
          {
            description: form.description,
            quantity: 1,
            unit: "kpl",
            unit_price: unitPrice,
            vat_percent: parseFloat(form.vat_percent),
            total: 0,
          },
        ],
      };
      if (isEdit) await api.put(`/quotes/recurring-invoices/items/${recurring.id}`, data);
      else await api.post("/quotes/recurring-invoices/items", data);
      toast.success(isEdit ? "Päivitetty" : "Toistuva lasku luotu");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  return (
    <div className="space-y-3" data-testid="recurring-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="text-xs text-muted-foreground">Nimi / kuvaus *</label>
          <Input value={form.name} onChange={set("name")} placeholder="Esim. Kuukausivuokra" className="mt-1" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Toistuvuus</label>
          <Select value={form.interval} onValueChange={(v) => setForm((f) => ({ ...f, interval: v }))}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(INTERVAL_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Seuraava laskupäivä</label>
          <Input type="date" value={form.next_invoice_date} onChange={set("next_invoice_date")} className="mt-1" />
        </div>
        <div className="col-span-2">
          <label className="text-xs text-muted-foreground">Asiakkaan nimi *</label>
          <Input value={form.recipient_name} onChange={set("recipient_name")} placeholder="Asiakas Oy" className="mt-1" />
        </div>
        <div className="col-span-2">
          <label className="text-xs text-muted-foreground">Asiakkaan sähköposti</label>
          <Input value={form.recipient_email} onChange={set("recipient_email")} placeholder="asiakas@email.fi" className="mt-1" />
        </div>
        <div className="col-span-2">
          <label className="text-xs text-muted-foreground">Laskurivi kuvaus *</label>
          <Input value={form.description} onChange={set("description")} placeholder="Palvelun kuvaus" className="mt-1" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Yksikköhinta (€) *</label>
          <Input type="number" step="0.01" value={form.amount} onChange={set("amount")} placeholder="0.00" className="mt-1" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">ALV %</label>
          <Input type="number" value={form.vat_percent} onChange={set("vat_percent")} className="mt-1" />
        </div>
        <div className="col-span-2 flex items-center gap-3">
          <Switch
            checked={form.is_active}
            onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
          />
          <span className="text-sm">{form.is_active ? "Aktiivinen" : "Pysäytetty"}</span>
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button variant="outline" onClick={onClose} className="flex-1">
          Peruuta
        </Button>
        <Button onClick={save} disabled={loading} className="flex-1 gap-2 bg-amber-500 hover:bg-amber-400 text-zinc-900" data-testid="recurring-save">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
          {isEdit ? "Tallenna" : "Luo toistuva lasku"}
        </Button>
      </div>
    </div>
  );
}

export default function RecurringInvoicesPanel({ settings, onInvoiceCreated }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [creatingId, setCreatingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/quotes/recurring-invoices/items");
      setItems(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Poistetaanko?")) return;
    try {
      await api.delete(`/quotes/recurring-invoices/items/${id}`);
      toast.success("Poistettu");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleActive = async (r, v) => {
    try {
      await api.put(`/quotes/recurring-invoices/items/${r.id}`, { ...r, is_active: v });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const createNow = async (r) => {
    setCreatingId(r.id);
    try {
      const res = await api.post(`/quotes/recurring-invoices/${r.id}/generate-now`);
      toast.success(`Lasku #${res.data.invoice_number} luotu`);
      onInvoiceCreated && onInvoiceCreated();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setCreatingId(null);
  };

  const today = new Date().toISOString().split("T")[0];
  const dueSoon = items.filter((r) => r.is_active && r.next_invoice_date && r.next_invoice_date <= today);

  return (
    <div className="space-y-4" data-testid="recurring-panel">
      {dueSoon.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-sm text-amber-300">
            <strong>{dueSoon.length} toistuvaa laskua</strong> odottaa luomista tänään!
          </p>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          size="sm"
          className="gap-2 bg-amber-500 hover:bg-amber-400 text-zinc-900"
          onClick={() => {
            setEditItem(null);
            setShowForm(true);
          }}
          data-testid="recurring-new"
        >
          <Plus className="w-4 h-4" /> Uusi toistuva lasku
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Ladataan...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Repeat className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p>Ei toistuvia laskuja. Lisää esim. kuukausittainen lasku!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <Card key={r.id} className={!r.is_active ? "opacity-60" : ""}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Switch checked={r.is_active} onCheckedChange={(v) => toggleActive(r, v)} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{r.name}</p>
                    <div className="flex flex-wrap gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground">{r.recipient_name}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {INTERVAL_LABELS[r.interval]}
                      </Badge>
                      <span
                        className={`text-xs ${
                          r.next_invoice_date <= today ? "text-amber-400 font-semibold" : "text-muted-foreground"
                        }`}
                      >
                        Seuraava: {r.next_invoice_date}
                      </span>
                      <span className="text-xs text-muted-foreground">Luotu: {r.invoices_created || 0} kpl</span>
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="font-semibold text-sm">{(Number(r.total_amount) || 0).toFixed(2)} €</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button
                      size="icon"
                      variant="outline"
                      className="w-7 h-7 text-emerald-400"
                      title="Luo lasku nyt"
                      onClick={() => createNow(r)}
                      disabled={creatingId === r.id}
                      data-testid={`recurring-run-${r.id}`}
                    >
                      {creatingId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="w-7 h-7"
                      onClick={() => {
                        setEditItem(r);
                        setShowForm(true);
                      }}
                    >
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="w-7 h-7 text-destructive/60 hover:text-destructive"
                      onClick={() => remove(r.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editItem ? "Muokkaa toistuvaa laskua" : "Uusi toistuva lasku"}</DialogTitle>
          </DialogHeader>
          <RecurringForm
            recurring={editItem}
            settings={settings}
            onSaved={() => {
              setShowForm(false);
              load();
            }}
            onClose={() => setShowForm(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
